import type { CSSProperties, ReactNode } from 'react'

/**
 * A chip you can turn on and off, where several may be on at once.
 *
 * The sibling of `TabGroup`, and the distinction is the whole reason both
 * exist: a TabGroup is one-of-N, so its selected state belongs to the group's
 * sliding indicator. These are many-of-N — claiming three departments, ticking
 * four disciplines — so there is nothing to slide and each chip paints its own
 * selected state.
 *
 * Not a `Tick` either: that is the square checkbox on a row in a list you are
 * reading down. This is for a set of short labels you are choosing among.
 *
 * The on treatment is the same accent wash the TabGroup indicator and
 * `FilterChip` use, so a selected chip reads the same whichever of the three
 * put it on screen — which is the point of it being a component rather than
 * the eleven lines of inline style it replaced in two places.
 */
export function ToggleChip({
  on,
  onClick,
  children,
  disabled = false,
  unavailable = false,
  title,
  style,
}: {
  on: boolean
  onClick: () => void
  children: ReactNode
  disabled?: boolean
  /**
   * Shown, but not something this account can turn on — a schedule column no
   * discipline of theirs covers. Rendered as a dashed outline with no control
   * behind it, because a disabled button still says "you could press this".
   */
  unavailable?: boolean
  title?: string
  style?: CSSProperties
}) {
  const BASE: CSSProperties = {
    padding: '6px 12px',
    borderRadius: 99,
    font: '500 11px Manrope,sans-serif',
    whiteSpace: 'nowrap',
    flex: 'none',
  }

  if (unavailable) {
    return (
      <span
        title={title}
        style={{
          ...BASE,
          border: '1px dashed var(--w-line-strong)',
          color: 'var(--color-faint)',
          ...style,
        }}
      >
        {children}
      </span>
    )
  }

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      disabled={disabled}
      title={title}
      onClick={onClick}
      // .b3ds only when off: the contained treatment is what makes an unchosen
      // chip read as pressable. On, the accent wash is doing that job and the
      // two together would fight.
      className={`tab ${on ? '' : 'b3ds'}`}
      style={{
        ...BASE,
        border: on ? '1px solid var(--w-pill-line)' : '1px solid var(--w-line-strong)',
        // Never `background`: the shorthand resets background-image and takes
        // .b3ds's gradient with it. See the .b3d block in application.css.
        backgroundColor: on ? 'var(--w-pill-fill)' : 'transparent',
        fontWeight: on ? 600 : 500,
        color: on ? 'var(--w-pill-ink)' : 'var(--color-muted)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  )
}
